/**
 * Year 12 ICT - Unit Exercises and Assignments
 * Exercises for every unit + Assignments for practical units
 */

// ============================================
// UNIT EXERCISES (For Every Unit)
// ============================================
export const year12ICTExercises = {
  // ==========================================
  // UNIT 1: PC Hardware Components
  // ==========================================
  unit1: {
    unitTitle: "Unit 1: PC Hardware Components",
    exercises: [
      {
        id: "ex1-1",
        title: "Exercise 1.1: Hardware Identification",
        type: "labeling",
        instructions: "Label all components in the diagram of a desktop PC interior.",
        tasks: [
          "Identify and label the CPU location",
          "Identify and label RAM slots and modules",
          "Identify and label the motherboard",
          "Identify and label storage devices (HDD/SSD)",
          "Identify and label the power supply unit",
          "Identify and label expansion slots (PCIe)",
          "Identify and label cooling components"
        ],
        materials: "PC interior diagram or actual computer"
      },
      {
        id: "ex1-2",
        title: "Exercise 1.2: Port and Cable Matching",
        type: "matching",
        instructions: "Match each port type to its common use and cable type.",
        items: [
          { port: "USB 3.0", use: "Peripherals, data transfer", cable: "USB Type-A/B/C" },
          { port: "HDMI", use: "Video and audio output", cable: "HDMI cable" },
          { port: "Ethernet (RJ-45)", use: "Network connection", cable: "CAT5e/CAT6" },
          { port: "3.5mm Audio", use: "Headphones/speakers", cable: "3.5mm audio cable" },
          { port: "DisplayPort", use: "High-res video output", cable: "DisplayPort cable" },
          { port: "SATA", use: "Internal storage", cable: "SATA data cable" },
          { port: "VGA", use: "Older monitors", cable: "VGA cable" }
        ]
      },
      {
        id: "ex1-3",
        title: "Exercise 1.3: Component Specifications",
        type: "research",
        instructions: "Research and complete the specifications table for a gaming PC.",
        questions: [
          "What CPU speed (GHz) is recommended for modern gaming?",
          "How much RAM is typically needed for gaming in 2025?",
          "What is the difference between HDD and SSD storage?",
          "What factors determine graphics card performance?",
          "What wattage PSU would support a mid-range gaming PC?"
        ]
      },
      {
        id: "ex1-4",
        title: "Exercise 1.4: Troubleshooting Scenarios",
        type: "problem-solving",
        instructions: "Identify the likely problem and solution for each scenario.",
        scenarios: [
          "Computer turns on but screen stays black",
          "Computer keeps restarting randomly",
          "Computer is very slow to start",
          "USB devices not being recognized",
          "Strange clicking noise from inside the computer"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 2: Building a PC
  // ==========================================
  unit2: {
    unitTitle: "Unit 2: Building a PC",
    exercises: [
      {
        id: "ex2-1",
        title: "Exercise 2.1: Component Ordering",
        type: "sequencing",
        instructions: "Arrange the following steps in the correct order for building a PC.",
        steps: [
          "Install the CPU cooler",
          "Install the power supply",
          "Install the CPU on motherboard",
          "Connect all power cables",
          "Install RAM modules",
          "Install the motherboard in case",
          "Install the I/O shield",
          "Install storage devices",
          "Install the graphics card",
          "Connect front panel cables"
        ],
        correctOrder: [3, 5, 1, 7, 6, 2, 8, 9, 4, 10]
      },
      {
        id: "ex2-2",
        title: "Exercise 2.2: Safety Checklist",
        type: "checklist",
        instructions: "Complete the PC building safety checklist.",
        items: [
          "Anti-static wrist strap worn or case grounded",
          "Working on non-conductive surface",
          "PSU unplugged during assembly",
          "Handling components by edges",
          "Adequate lighting in workspace",
          "Tools organized and ready",
          "Manual/documentation available",
          "No food or drinks near workspace"
        ]
      },
      {
        id: "ex2-3",
        title: "Exercise 2.3: POST Beep Codes",
        type: "research",
        instructions: "Research common POST beep codes for your motherboard brand and document their meanings.",
        tasks: [
          "Find the beep code for 'no RAM detected'",
          "Find the beep code for 'graphics card error'",
          "Find the beep code for 'CPU not detected'",
          "Find the beep code for 'successful POST'",
          "Document steps to resolve each error"
        ]
      },
      {
        id: "ex2-4",
        title: "Exercise 2.4: Cable Management Plan",
        type: "planning",
        instructions: "Create a cable management plan for a PC build.",
        tasks: [
          "Identify all cables that need to be routed",
          "Plan which cables go behind the motherboard tray",
          "Plan front panel cable routing",
          "Identify tie-down points in the case",
          "Consider airflow in your routing plan"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 3: Operating System Installation
  // ==========================================
  unit3: {
    unitTitle: "Unit 3: Operating System Installation",
    exercises: [
      {
        id: "ex3-1",
        title: "Exercise 3.1: OS Comparison",
        type: "comparison",
        instructions: "Compare Windows, macOS, and Linux operating systems.",
        criteria: [
          "Cost (free vs. paid)",
          "User interface",
          "Software compatibility",
          "Hardware requirements",
          "Security features",
          "Target users",
          "Customization options"
        ]
      },
      {
        id: "ex3-2",
        title: "Exercise 3.2: BIOS Navigation",
        type: "practical",
        instructions: "Navigate through BIOS/UEFI and identify key settings.",
        tasks: [
          "Find the boot order settings",
          "Locate CPU temperature reading",
          "Find RAM speed and configuration",
          "Locate secure boot settings",
          "Find date and time settings",
          "Identify how to save and exit"
        ]
      },
      {
        id: "ex3-3",
        title: "Exercise 3.3: Driver Research",
        type: "research",
        instructions: "For a given computer, identify and download required drivers.",
        tasks: [
          "Identify motherboard model and find chipset drivers",
          "Find graphics card driver download page",
          "Locate network adapter drivers",
          "Find audio drivers",
          "Document driver version numbers"
        ]
      },
      {
        id: "ex3-4",
        title: "Exercise 3.4: Partition Planning",
        type: "planning",
        instructions: "Plan partition layout for a 500GB drive.",
        scenarios: [
          "Single OS with data partition",
          "Dual boot (Windows and Linux)",
          "OS partition with separate programs and data partitions"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 5: Advanced Word Processing
  // ==========================================
  unit5: {
    unitTitle: "Unit 5: Advanced Word Processing",
    exercises: [
      {
        id: "ex5-1",
        title: "Exercise 5.1: Formatting Practice",
        type: "practical",
        instructions: "Apply various formatting to a provided document.",
        tasks: [
          "Change title to Heading 1 style",
          "Apply 1.5 line spacing to body text",
          "Create a bulleted list with 5 items",
          "Create a numbered list with 5 steps",
          "Add a border around one paragraph",
          "Set first paragraph indent to 1cm",
          "Apply drop cap to first letter"
        ]
      },
      {
        id: "ex5-2",
        title: "Exercise 5.2: Table Creation",
        type: "practical",
        instructions: "Create and format a class timetable.",
        requirements: [
          "5 columns (Monday to Friday)",
          "8 rows (time periods)",
          "Header row with days",
          "Time column on left",
          "Apply shading to header",
          "Merge cells for lunch break",
          "Apply borders to all cells"
        ]
      },
      {
        id: "ex5-3",
        title: "Exercise 5.3: Styles Application",
        type: "practical",
        instructions: "Apply and modify styles in a research report.",
        tasks: [
          "Apply Heading 1 to main title",
          "Apply Heading 2 to section titles",
          "Modify Heading 1 style color",
          "Create a custom style for quotes",
          "Apply Normal style to body text",
          "Generate a table of contents"
        ]
      },
      {
        id: "ex5-4",
        title: "Exercise 5.4: Document Layout",
        type: "practical",
        instructions: "Set up a professional document layout.",
        tasks: [
          "Set page margins to 2.5cm all sides",
          "Add header with document title",
          "Add footer with page numbers",
          "Insert a cover page",
          "Add page break before references",
          "Set paper size to A4"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 6: Mail Merge & Citations
  // ==========================================
  unit6: {
    unitTitle: "Unit 6: Mail Merge & Citations",
    exercises: [
      {
        id: "ex6-1",
        title: "Exercise 6.1: Data Source Creation",
        type: "practical",
        instructions: "Create a data source for a mail merge.",
        fields: ["Title", "FirstName", "LastName", "Address", "City", "PostCode", "AccountNumber"],
        requirement: "Enter at least 5 records"
      },
      {
        id: "ex6-2",
        title: "Exercise 6.2: Merge Field Practice",
        type: "practical",
        instructions: "Insert merge fields into a template letter.",
        tasks: [
          "Insert greeting with Title and LastName",
          "Insert full address block",
          "Insert AccountNumber in the body",
          "Add an IF field for overdue accounts",
          "Preview the merge results"
        ]
      },
      {
        id: "ex6-3",
        title: "Exercise 6.3: Citation Practice",
        type: "practical",
        instructions: "Add citations to an academic paragraph.",
        sources: [
          "Book by single author",
          "Book by multiple authors",
          "Website article",
          "Journal article",
          "Generate bibliography"
        ]
      },
      {
        id: "ex6-4",
        title: "Exercise 6.4: Footnotes and Endnotes",
        type: "practical",
        instructions: "Add footnotes and endnotes to a document.",
        tasks: [
          "Add 3 footnotes explaining terms",
          "Add 2 endnotes with additional information",
          "Format footnotes in a consistent style",
          "Convert one footnote to an endnote"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 7: Spreadsheet Fundamentals
  // ==========================================
  unit7: {
    unitTitle: "Unit 7: Spreadsheet Fundamentals",
    exercises: [
      {
        id: "ex7-1",
        title: "Exercise 7.1: Basic Formulas",
        type: "practical",
        instructions: "Write formulas to calculate the following:",
        problems: [
          "Sum of cells A1 to A20",
          "Average of B1 to B15",
          "Maximum value in C1 to C10",
          "Minimum value in D1 to D10",
          "Count of numbers in E1 to E25"
        ]
      },
      {
        id: "ex7-2",
        title: "Exercise 7.2: Cell Referencing",
        type: "practical",
        instructions: "Use appropriate cell references for these scenarios:",
        scenarios: [
          "Calculate price × quantity (both should change when copied)",
          "Calculate all prices with the same tax rate in cell F1",
          "Calculate with a fixed row but changing column",
          "Calculate with a fixed column but changing row"
        ]
      },
      {
        id: "ex7-3",
        title: "Exercise 7.3: Formatting",
        type: "practical",
        instructions: "Apply formatting to a financial spreadsheet:",
        tasks: [
          "Format currency column with $ symbol",
          "Format percentage column to 2 decimal places",
          "Format date column as DD/MM/YYYY",
          "Apply conditional formatting for negative numbers",
          "Add borders to the data table"
        ]
      },
      {
        id: "ex7-4",
        title: "Exercise 7.4: IF Function Practice",
        type: "practical",
        instructions: "Write IF formulas for these conditions:",
        problems: [
          "If score >= 50, display 'Pass', otherwise 'Fail'",
          "If quantity < 10, display 'Reorder', otherwise 'OK'",
          "Nested IF: A (>=85), B (>=70), C (>=50), F (<50)",
          "If cell is empty, display 'No Data', otherwise show value"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 8: Charts & Data Analysis
  // ==========================================
  unit8: {
    unitTitle: "Unit 8: Charts & Data Analysis",
    exercises: [
      {
        id: "ex8-1",
        title: "Exercise 8.1: Chart Type Selection",
        type: "matching",
        instructions: "Match each data scenario with the best chart type:",
        scenarios: [
          { data: "Monthly sales over one year", chartType: "Line chart" },
          { data: "Market share percentages", chartType: "Pie chart" },
          { data: "Comparing sales across 5 stores", chartType: "Bar chart" },
          { data: "Correlation between study hours and grades", chartType: "Scatter plot" },
          { data: "Budget vs. actual spending by category", chartType: "Grouped bar chart" }
        ]
      },
      {
        id: "ex8-2",
        title: "Exercise 8.2: Chart Creation",
        type: "practical",
        instructions: "Create charts from provided data:",
        tasks: [
          "Create a line chart showing 12 months of data",
          "Add chart title and axis labels",
          "Add a legend",
          "Format the data series",
          "Add data labels to key points"
        ]
      },
      {
        id: "ex8-3",
        title: "Exercise 8.3: Chart Formatting",
        type: "practical",
        instructions: "Format an existing chart:",
        tasks: [
          "Change chart colors to match company branding",
          "Modify axis scale",
          "Move legend to bottom",
          "Add a trendline",
          "Format chart area background"
        ]
      },
      {
        id: "ex8-4",
        title: "Exercise 8.4: Data Analysis",
        type: "analysis",
        instructions: "Analyze the chart and answer questions:",
        questions: [
          "What trend does the data show?",
          "What is the highest and lowest point?",
          "What percentage does each category represent?",
          "What predictions can be made based on the trend?",
          "What additional data would improve the analysis?"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 9: Database Fundamentals
  // ==========================================
  unit9: {
    unitTitle: "Unit 9: Database Fundamentals",
    exercises: [
      {
        id: "ex9-1",
        title: "Exercise 9.1: Database Terminology",
        type: "matching",
        instructions: "Match terms to their definitions:",
        terms: [
          { term: "Table", definition: "Collection of related records" },
          { term: "Record", definition: "A single row of data" },
          { term: "Field", definition: "A single column/attribute" },
          { term: "Primary Key", definition: "Unique identifier for each record" },
          { term: "Foreign Key", definition: "Links to primary key in another table" }
        ]
      },
      {
        id: "ex9-2",
        title: "Exercise 9.2: Data Type Selection",
        type: "practical",
        instructions: "Choose appropriate data types for these fields:",
        fields: [
          { field: "StudentID", suggestedType: "AutoNumber/Integer" },
          { field: "FirstName", suggestedType: "Text (50 characters)" },
          { field: "DateOfBirth", suggestedType: "Date/Time" },
          { field: "Email", suggestedType: "Text (100 characters)" },
          { field: "GPA", suggestedType: "Number (Decimal)" },
          { field: "IsActive", suggestedType: "Yes/No (Boolean)" },
          { field: "ProfilePhoto", suggestedType: "OLE Object/Attachment" }
        ]
      },
      {
        id: "ex9-3",
        title: "Exercise 9.3: Table Design",
        type: "design",
        instructions: "Design tables for a school database:",
        requirements: [
          "Students table with at least 6 fields",
          "Classes table with at least 4 fields",
          "Enrollments table to link students and classes",
          "Identify primary keys for each table",
          "Identify foreign key relationships"
        ]
      },
      {
        id: "ex9-4",
        title: "Exercise 9.4: Relationship Types",
        type: "identification",
        instructions: "Identify the relationship type for each scenario:",
        scenarios: [
          "One student has one student ID card",
          "One teacher teaches many classes",
          "Students can enroll in many classes, classes have many students",
          "One department has many employees",
          "One order has many order items"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 10: Forms, Queries & Reports
  // ==========================================
  unit10: {
    unitTitle: "Unit 10: Forms, Queries & Reports",
    exercises: [
      {
        id: "ex10-1",
        title: "Exercise 10.1: Basic SQL Queries",
        type: "practical",
        instructions: "Write SQL queries for these requirements:",
        queries: [
          "Select all records from Students table",
          "Select only FirstName and LastName from Students",
          "Select students where Age > 16",
          "Select students ordered by LastName A-Z",
          "Select students in 'Year 12' class"
        ]
      },
      {
        id: "ex10-2",
        title: "Exercise 10.2: Advanced SQL",
        type: "practical",
        instructions: "Write more complex SQL queries:",
        queries: [
          "Count total students in each class",
          "Find average GPA by class",
          "Select students with GPA > 3.0 AND Age >= 17",
          "Select students in 'Year 11' OR 'Year 12'",
          "Find the student with highest GPA"
        ]
      },
      {
        id: "ex10-3",
        title: "Exercise 10.3: Form Design",
        type: "practical",
        instructions: "Design a data entry form with:",
        elements: [
          "Text boxes for name fields",
          "Date picker for date of birth",
          "Drop-down list for class selection",
          "Checkbox for active status",
          "Navigation buttons",
          "Form header with title"
        ]
      },
      {
        id: "ex10-4",
        title: "Exercise 10.4: Report Design",
        type: "practical",
        instructions: "Create a report with:",
        elements: [
          "Report header with title and date",
          "Grouping by class",
          "Calculated field for totals",
          "Page numbers in footer",
          "Professional formatting"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 11: Digital Image Processing
  // ==========================================
  unit11: {
    unitTitle: "Unit 11: Digital Image Processing",
    exercises: [
      {
        id: "ex11-1",
        title: "Exercise 11.1: Image Format Comparison",
        type: "research",
        instructions: "Complete the comparison table:",
        formats: ["JPEG", "PNG", "GIF", "BMP", "TIFF", "SVG"],
        criteria: ["Compression type", "Transparency support", "Animation support", "Best use case", "File size"]
      },
      {
        id: "ex11-2",
        title: "Exercise 11.2: Basic Editing",
        type: "practical",
        instructions: "Perform these edits on a provided image:",
        tasks: [
          "Crop to focus on main subject",
          "Resize to 800x600 pixels",
          "Adjust brightness (+10)",
          "Adjust contrast (+15)",
          "Apply auto color correction"
        ]
      },
      {
        id: "ex11-3",
        title: "Exercise 11.3: Layer Practice",
        type: "practical",
        instructions: "Create a composition using layers:",
        tasks: [
          "Create background layer",
          "Add image layer on top",
          "Add text layer",
          "Adjust layer opacity",
          "Apply layer blending mode"
        ]
      },
      {
        id: "ex11-4",
        title: "Exercise 11.4: Collage Creation",
        type: "practical",
        instructions: "Create a photo collage:",
        requirements: [
          "Use 4-6 images",
          "Arrange in creative layout",
          "Add borders between images",
          "Include title text",
          "Apply consistent filter/style"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 12: Programming Fundamentals
  // ==========================================
  unit12: {
    unitTitle: "Unit 12: Programming Fundamentals",
    exercises: [
      {
        id: "ex12-1",
        title: "Exercise 12.1: Variables Practice",
        type: "coding",
        instructions: "Write Python code to:",
        tasks: [
          "Declare a variable for your name",
          "Declare a variable for your age (as integer)",
          "Declare a variable for your GPA (as float)",
          "Print all variables in a sentence",
          "Calculate and print your age in 5 years"
        ]
      },
      {
        id: "ex12-2",
        title: "Exercise 12.2: Selection Practice",
        type: "coding",
        instructions: "Write if/else statements for:",
        problems: [
          "Check if a number is positive, negative, or zero",
          "Determine if a person can vote (age >= 18)",
          "Grade calculator: A (>=85), B (>=70), C (>=50), F (<50)",
          "Check if a year is a leap year",
          "Determine shipping cost based on weight"
        ]
      },
      {
        id: "ex12-3",
        title: "Exercise 12.3: Loop Practice",
        type: "coding",
        instructions: "Write loops to:",
        problems: [
          "Print numbers 1 to 10",
          "Print even numbers from 2 to 20",
          "Calculate sum of numbers 1 to 100",
          "Print each character in a string",
          "Create multiplication table for a number"
        ]
      },
      {
        id: "ex12-4",
        title: "Exercise 12.4: List Practice",
        type: "coding",
        instructions: "Work with Python lists:",
        tasks: [
          "Create a list of 5 student names",
          "Add a new student to the list",
          "Remove a student from the list",
          "Print each student with a number",
          "Find and print the longest name"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 13: Computer Networks
  // ==========================================
  unit13: {
    unitTitle: "Unit 13: Computer Networks",
    exercises: [
      {
        id: "ex13-1",
        title: "Exercise 13.1: Network Terminology",
        type: "matching",
        instructions: "Match network terms to definitions:",
        terms: [
          { term: "Router", definition: "Connects different networks and routes data" },
          { term: "Switch", definition: "Connects devices within a network" },
          { term: "IP Address", definition: "Unique identifier for a device on network" },
          { term: "DNS", definition: "Converts domain names to IP addresses" },
          { term: "Firewall", definition: "Filters network traffic for security" }
        ]
      },
      {
        id: "ex13-2",
        title: "Exercise 13.2: IP Address Analysis",
        type: "practical",
        instructions: "Analyze IP addresses:",
        tasks: [
          "Identify your computer's IP address",
          "Identify your subnet mask",
          "Identify your default gateway",
          "Determine your DNS server addresses",
          "Classify whether IPs are public or private"
        ]
      },
      {
        id: "ex13-3",
        title: "Exercise 13.3: Network Diagram",
        type: "design",
        instructions: "Draw a network diagram for a small office:",
        requirements: [
          "5 desktop computers",
          "2 laptops (wireless)",
          "1 network printer",
          "1 switch",
          "1 wireless router",
          "Internet connection"
        ]
      },
      {
        id: "ex13-4",
        title: "Exercise 13.4: Cable Making",
        type: "practical",
        instructions: "Create an Ethernet cable:",
        steps: [
          "Cut CAT6 cable to required length",
          "Strip outer jacket about 2cm",
          "Arrange wires in T568B order",
          "Trim wires evenly",
          "Insert into RJ-45 connector",
          "Crimp the connector",
          "Test with cable tester"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 14: Web Page Creation
  // ==========================================
  unit14: {
    unitTitle: "Unit 14: Web Page Creation",
    exercises: [
      {
        id: "ex14-1",
        title: "Exercise 14.1: Website Planning",
        type: "planning",
        instructions: "Plan a website for a local business:",
        tasks: [
          "Define the purpose and target audience",
          "List required pages",
          "Create site map showing navigation",
          "Sketch wireframe for home page",
          "Choose color scheme and fonts"
        ]
      },
      {
        id: "ex14-2",
        title: "Exercise 14.2: CMS Exploration",
        type: "practical",
        instructions: "Explore a CMS platform:",
        tasks: [
          "Create a free account on WordPress.com or Wix",
          "Browse available templates",
          "Create a test page",
          "Add text and an image",
          "Publish and view the page"
        ]
      },
      {
        id: "ex14-3",
        title: "Exercise 14.3: Page Content",
        type: "practical",
        instructions: "Create page content:",
        elements: [
          "Add a header with site logo",
          "Create navigation menu",
          "Add hero section with image",
          "Create content sections",
          "Add footer with contact info"
        ]
      },
      {
        id: "ex14-4",
        title: "Exercise 14.4: Website Review",
        type: "analysis",
        instructions: "Review and critique a website:",
        criteria: [
          "Navigation ease of use",
          "Visual design and consistency",
          "Content quality and relevance",
          "Mobile responsiveness",
          "Loading speed",
          "Accessibility features"
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 15: Internet Security & Safety
  // ==========================================
  unit15: {
    unitTitle: "Unit 15: Internet Security & Safety",
    exercises: [
      {
        id: "ex15-1",
        title: "Exercise 15.1: Threat Identification",
        type: "matching",
        instructions: "Match cyber threats to their descriptions:",
        threats: [
          { threat: "Phishing", description: "Fake emails to steal credentials" },
          { threat: "Ransomware", description: "Encrypts files for ransom" },
          { threat: "Malware", description: "Malicious software" },
          { threat: "DDoS", description: "Overwhelms servers with traffic" },
          { threat: "Social Engineering", description: "Manipulating people for information" }
        ]
      },
      {
        id: "ex15-2",
        title: "Exercise 15.2: Password Audit",
        type: "analysis",
        instructions: "Evaluate password strength:",
        passwords: [
          "password123",
          "MyD0g$Nam3!sMax",
          "123456789",
          "Tr0ub4dor&3",
          "qwertyuiop"
        ],
        criteria: ["Length", "Complexity", "Predictability", "Overall rating"]
      },
      {
        id: "ex15-3",
        title: "Exercise 15.3: Phishing Detection",
        type: "analysis",
        instructions: "Identify phishing indicators in sample emails:",
        lookFor: [
          "Sender email address",
          "Greeting type",
          "Urgency/threats",
          "Link destinations",
          "Grammar/spelling",
          "Requests for information"
        ]
      },
      {
        id: "ex15-4",
        title: "Exercise 15.4: Security Checklist",
        type: "audit",
        instructions: "Complete a personal security audit:",
        items: [
          "Using different passwords for accounts",
          "Two-factor authentication enabled",
          "Antivirus software installed and updated",
          "Regular data backups",
          "Browser and OS updated",
          "Secure WiFi connection",
          "Privacy settings reviewed"
        ]
      }
    ]
  }
};


// ============================================
// PRACTICAL ASSIGNMENTS
// ============================================
export const year12ICTAssignments = {
  // ==========================================
  // ASSIGNMENT 1: Build a PC (Unit 2)
  // ==========================================
  assignment1: {
    id: "assign-1",
    unitTitle: "Unit 2: Building a PC",
    title: "PC Building Project",
    type: "Practical Project",
    duration: "2 weeks",
    totalMarks: 100,
    overview: "Plan, assemble, and configure a working personal computer from components.",
    
    objectives: [
      "Demonstrate understanding of PC components and their functions",
      "Apply safe handling procedures for computer hardware",
      "Successfully assemble a working PC",
      "Install and configure an operating system"
    ],
    
    requirements: {
      part1: {
        title: "Part A: Planning (20 marks)",
        tasks: [
          "Select compatible components for a specific use case (gaming, office, etc.)",
          "Create a component list with prices and specifications",
          "Justify each component selection",
          "Identify potential compatibility issues"
        ],
        deliverable: "Written report (2-3 pages)"
      },
      part2: {
        title: "Part B: Assembly (50 marks)",
        tasks: [
          "Demonstrate proper safety procedures (10 marks)",
          "Install CPU and cooler correctly (10 marks)",
          "Install RAM in correct slots (5 marks)",
          "Mount motherboard in case (5 marks)",
          "Connect all power and data cables (10 marks)",
          "Install storage devices (5 marks)",
          "Cable management (5 marks)"
        ],
        deliverable: "Teacher observation during assembly"
      },
      part3: {
        title: "Part C: Configuration (30 marks)",
        tasks: [
          "Configure BIOS settings (5 marks)",
          "Install operating system (10 marks)",
          "Install device drivers (5 marks)",
          "Install essential software (5 marks)",
          "Verify all components are working (5 marks)"
        ],
        deliverable: "Working PC with documentation"
      }
    },
    
    rubric: {
      excellent: "90-100: All components correctly assembled, excellent documentation, professional cable management, OS fully configured",
      good: "70-89: PC assembled correctly, good documentation, minor issues with cable management or configuration",
      satisfactory: "50-69: PC functional but with some assembly or configuration issues, basic documentation",
      needsImprovement: "Below 50: PC not functional, missing documentation, safety procedures not followed"
    }
  },

  // ==========================================
  // ASSIGNMENT 2: Database Project (Unit 9-10)
  // ==========================================
  assignment2: {
    id: "assign-2",
    unitTitle: "Units 9-10: Database",
    title: "Database Design and Implementation Project",
    type: "Practical Project",
    duration: "2 weeks",
    totalMarks: 100,
    overview: "Design and implement a relational database for a real-world scenario.",
    
    objectives: [
      "Apply database design principles",
      "Create properly normalized tables",
      "Implement relationships between tables",
      "Create forms, queries, and reports"
    ],
    
    scenarios: [
      "School Library Management System",
      "Small Business Inventory System",
      "Sports Club Membership Database",
      "Student Assignment Tracking System"
    ],
    
    requirements: {
      part1: {
        title: "Part A: Design (30 marks)",
        tasks: [
          "Identify entities and attributes (10 marks)",
          "Create Entity-Relationship Diagram (10 marks)",
          "Normalize to at least 2NF (5 marks)",
          "Document data types and constraints (5 marks)"
        ],
        deliverable: "Design document with ERD"
      },
      part2: {
        title: "Part B: Implementation (40 marks)",
        tasks: [
          "Create database with at least 3 linked tables (15 marks)",
          "Define relationships with referential integrity (10 marks)",
          "Enter sample data (minimum 10 records per table) (5 marks)",
          "Implement data validation rules (10 marks)"
        ],
        deliverable: "Database file"
      },
      part3: {
        title: "Part C: User Interface (30 marks)",
        tasks: [
          "Create data entry form (10 marks)",
          "Create at least 3 queries (one with criteria, one with calculation, one with grouping) (10 marks)",
          "Create a formatted report (10 marks)"
        ],
        deliverable: "Forms, queries, and reports in database"
      }
    },
    
    rubric: {
      excellent: "90-100: Well-designed normalized database, all relationships working, professional forms and reports",
      good: "70-89: Functional database with minor design issues, most features working correctly",
      satisfactory: "50-69: Basic database functionality, some relationship or form issues",
      needsImprovement: "Below 50: Database incomplete or not functional"
    }
  },

  // ==========================================
  // ASSIGNMENT 3: Programming Project (Unit 12)
  // ==========================================
  assignment3: {
    id: "assign-3",
    unitTitle: "Unit 12: Programming",
    title: "Python Programming Project",
    type: "Practical Project",
    duration: "2 weeks",
    totalMarks: 100,
    overview: "Develop a Python program that solves a practical problem using structured programming concepts.",
    
    objectives: [
      "Apply structured programming concepts",
      "Use variables, selection, and repetition correctly",
      "Implement functions for code organization",
      "Follow software development methodology"
    ],
    
    projectOptions: [
      "Student Grade Calculator with file storage",
      "Simple Quiz Game with scoring",
      "Personal Budget Tracker",
      "To-Do List Manager",
      "Simple Text-Based Adventure Game"
    ],
    
    requirements: {
      part1: {
        title: "Part A: Planning & Design (25 marks)",
        tasks: [
          "Problem definition and requirements (5 marks)",
          "Algorithm design using flowchart or pseudocode (10 marks)",
          "Test plan with expected inputs/outputs (5 marks)",
          "Variable and function planning (5 marks)"
        ],
        deliverable: "Design document"
      },
      part2: {
        title: "Part B: Implementation (50 marks)",
        tasks: [
          "Working program that meets requirements (20 marks)",
          "Use of variables with appropriate data types (5 marks)",
          "Correct use of selection statements (if/else) (10 marks)",
          "Correct use of loops (for/while) (10 marks)",
          "Use of at least 2 functions (5 marks)"
        ],
        deliverable: "Python source code file"
      },
      part3: {
        title: "Part C: Testing & Documentation (25 marks)",
        tasks: [
          "Test with various inputs (10 marks)",
          "Code comments explaining logic (5 marks)",
          "User documentation (how to use the program) (5 marks)",
          "Reflection on challenges and solutions (5 marks)"
        ],
        deliverable: "Test results and documentation"
      }
    },
    
    codeRequirements: [
      "At least 50 lines of code",
      "Input from user",
      "Output meaningful results",
      "Handle invalid inputs gracefully",
      "Use meaningful variable names",
      "Include comments"
    ],
    
    rubric: {
      excellent: "90-100: Program fully functional, well-structured code, comprehensive documentation, handles all edge cases",
      good: "70-89: Program works correctly, good code structure, minor documentation gaps",
      satisfactory: "50-69: Program mostly works, some bugs or missing features, basic documentation",
      needsImprovement: "Below 50: Program not functional or missing major requirements"
    }
  },

  // ==========================================
  // ASSIGNMENT 4: Website Project (Unit 14)
  // ==========================================
  assignment4: {
    id: "assign-4",
    unitTitle: "Unit 14: Web Page Creation",
    title: "Website Development Project",
    type: "Practical Project",
    duration: "2 weeks",
    totalMarks: 100,
    overview: "Plan, design, and create a multi-page website using a Content Management System.",
    
    objectives: [
      "Apply web design principles",
      "Use a CMS effectively",
      "Create user-friendly navigation",
      "Publish a functional website"
    ],
    
    websiteTypes: [
      "Personal Portfolio/Resume website",
      "School Club or Sports Team website",
      "Small Business website",
      "Educational Resource website",
      "Community Organization website"
    ],
    
    requirements: {
      part1: {
        title: "Part A: Planning (20 marks)",
        tasks: [
          "Define purpose and target audience (5 marks)",
          "Create site map with at least 5 pages (5 marks)",
          "Create wireframe sketches for 2 key pages (5 marks)",
          "Plan color scheme and typography (5 marks)"
        ],
        deliverable: "Planning document with sketches"
      },
      part2: {
        title: "Part B: Development (50 marks)",
        tasks: [
          "Home page with clear purpose (10 marks)",
          "At least 4 additional pages (10 marks)",
          "Navigation menu on all pages (5 marks)",
          "Relevant images and media (10 marks)",
          "Contact form or information (5 marks)",
          "Consistent design throughout (10 marks)"
        ],
        deliverable: "Published website"
      },
      part3: {
        title: "Part C: Testing & Evaluation (30 marks)",
        tasks: [
          "Test all navigation links (5 marks)",
          "Test on different devices/browsers (10 marks)",
          "Peer review and feedback incorporation (10 marks)",
          "SEO basics implemented (5 marks)"
        ],
        deliverable: "Testing checklist and peer review forms"
      }
    },
    
    technicalRequirements: [
      "Minimum 5 pages",
      "Working navigation on all pages",
      "At least 10 images",
      "Consistent header and footer",
      "Contact method included",
      "Mobile-friendly design",
      "Published to live URL"
    ],
    
    rubric: {
      excellent: "90-100: Professional-looking website, excellent navigation, responsive design, all requirements met",
      good: "70-89: Good website with minor design inconsistencies, most requirements met",
      satisfactory: "50-69: Functional website with design issues, basic requirements met",
      needsImprovement: "Below 50: Website incomplete or not published"
    }
  },

  // ==========================================
  // ASSIGNMENT 5: Network Setup (Unit 13)
  // ==========================================
  assignment5: {
    id: "assign-5",
    unitTitle: "Unit 13: Computer Networks",
    title: "Small Network Setup Project",
    type: "Practical Project",
    duration: "1 week",
    totalMarks: 80,
    overview: "Design and configure a small peer-to-peer or client-server network.",
    
    objectives: [
      "Understand network components and their roles",
      "Configure network settings on computers",
      "Enable file and printer sharing",
      "Test network connectivity"
    ],
    
    requirements: {
      part1: {
        title: "Part A: Design (20 marks)",
        tasks: [
          "Network diagram showing all components (10 marks)",
          "IP addressing plan (5 marks)",
          "List of required hardware (5 marks)"
        ],
        deliverable: "Network design document"
      },
      part2: {
        title: "Part B: Implementation (40 marks)",
        tasks: [
          "Physical connections made correctly (10 marks)",
          "IP addresses configured on all devices (10 marks)",
          "File sharing enabled and working (10 marks)",
          "All computers can ping each other (10 marks)"
        ],
        deliverable: "Working network (teacher verification)"
      },
      part3: {
        title: "Part C: Documentation (20 marks)",
        tasks: [
          "Step-by-step configuration guide (10 marks)",
          "Troubleshooting common issues (5 marks)",
          "Network security recommendations (5 marks)"
        ],
        deliverable: "Documentation report"
      }
    },
    
    rubric: {
      excellent: "70-80: Network fully functional, comprehensive documentation, all devices communicating",
      good: "55-69: Network mostly working, good documentation, minor issues",
      satisfactory: "40-54: Basic network functionality, some configuration issues",
      needsImprovement: "Below 40: Network not functional or missing documentation"
    }
  }
};

export default { year12ICTExercises, year12ICTAssignments };
